import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Lock, Apple, Play, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export function PublicLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `font-medium transition-colors ${
      isActive
        ? 'text-brand-blue'
        : 'text-secondary-600 hover:text-brand-blue'
    }`;

  const bookTripTarget = isAuthenticated ? '/dashboard?tab=book' : '/signup';
  const profileActive = location.pathname === '/dashboard' && (location.search === '' || location.search.includes('tab=profile'));
  const featuresActive = location.hash === '#features';

  return (
    <div className="min-h-screen bg-brand-white flex flex-col font-sans text-secondary-900">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-md z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-brand-blue rounded-lg flex items-center justify-center">
                  <span className="text-brand-orange font-bold text-xl">W</span>
                </div>
                <span className="text-brand-blue font-bold text-2xl tracking-tight">
                  Wote <span className="text-brand-orange">InterCity</span>
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <NavLink to="/" end className={navClass}>Home</NavLink>
              <NavLink to="/routes" className={navClass}>Routes & Schedules</NavLink>
              <a href="/#features" className={`${featuresActive ? 'text-brand-blue' : 'text-secondary-600 hover:text-brand-blue'} transition-colors font-medium`}>Features</a>
              {isAuthenticated ? (
                <Link
                  to="/dashboard?tab=profile"
                  aria-label="Profile"
                  title="Profile"
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-full border transition-all ${
                    profileActive
                      ? 'border-brand-blue bg-brand-blue text-white shadow-sm'
                      : 'border-secondary-200 bg-white text-brand-blue hover:border-brand-blue/40 hover:bg-brand-blue/5'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="sr-only">Profile</span>
                </Link>
              ) : (
                <NavLink to="/login" className={navClass}>Sign In</NavLink>
              )}
              <Link
                to={bookTripTarget}
                className="bg-brand-orange text-white px-6 py-2.5 rounded-full font-semibold hover:bg-brand-orange/90 transition-all shadow-md hover:shadow-lg"
              >
                Book a Trip
              </Link>
            </div>

            {/* Mobile menu button (Simplified for now) */}
            <div className="md:hidden flex items-center gap-3">
              {isAuthenticated && (
                <Link
                  to="/dashboard?tab=profile"
                  aria-label="Profile"
                  title="Profile"
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
                    profileActive
                      ? 'border-brand-blue bg-brand-blue text-white'
                      : 'border-secondary-200 bg-white text-brand-blue hover:bg-brand-blue/5'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="sr-only">Profile</span>
                </Link>
              )}
              <Link
                to={bookTripTarget}
                className="bg-brand-orange text-white px-4 py-2 rounded-full font-medium hover:bg-brand-orange/90 transition-colors text-sm"
              >
                Book
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-brand-blue text-brand-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="col-span-1 md:col-span-12 lg:col-span-4">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <span className="text-white font-bold text-2xl tracking-tight">
                  Wote <span className="text-brand-orange">InterCity</span>
                </span>
              </Link>
              <p className="text-blue-100/80 max-w-sm">
                Redefining intercity travel with modern, comfortable, and reliable bus services across the region.
              </p>
            </div>
            
            <div className="col-span-1 md:col-span-4 lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-blue-100/80 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/careers" className="text-blue-100/80 hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/" className="text-blue-100/80 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div className="col-span-1 md:col-span-4 lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4 text-white">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-blue-100/80 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-blue-100/80 hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>

            <div className="col-span-1 md:col-span-4 lg:col-span-4">
              <h3 className="text-lg font-semibold mb-4 text-white">Get the App</h3>
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3">
                <a href="#" className="flex items-center justify-center gap-3 bg-black/20 hover:bg-black/40 border border-white/10 rounded-xl px-4 py-2 transition-all group w-full sm:w-auto">
                  <Apple className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] text-blue-100/80 leading-none mb-0.5">Download on the</span>
                    <span className="text-sm font-semibold text-white leading-none">App Store</span>
                  </div>
                </a>
                <a href="#" className="flex items-center justify-center gap-3 bg-black/20 hover:bg-black/40 border border-white/10 rounded-xl px-4 py-2 transition-all group w-full sm:w-auto">
                  <Play className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] text-blue-100/80 leading-none mb-0.5">GET IT ON</span>
                    <span className="text-sm font-semibold text-white leading-none">Google Play</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-blue-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-blue-200/60 text-sm">
              &copy; {new Date().getFullYear()} Wote InterCity. All rights reserved.
            </p>
            
            <Link 
              to="/admin" 
              className="flex items-center gap-1.5 text-blue-100/70 hover:text-white transition-colors text-sm font-medium"
              title="Admin Sign In"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Admin Sign In</span>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
